import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setUnauthorizedHandler, tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [activeCustomerId, setActiveCustomerIdState] = useState(tokenStore.getCustomer());
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    tokenStore.set(null);
    tokenStore.setCustomer(null);
    setUser(null);
    setCustomer(null);
    setCustomers([]);
    setActiveCustomerIdState(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => logout());
  }, [logout]);

  const loadMe = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data.user);
    setCustomer(data.customer);
    setCustomers(data.customers || []);
    if (data.user.role === "proxima_admin" && !tokenStore.getCustomer() && data.customers?.length) {
      // Default Proxima staff to the first customer so the portal shows a real portfolio
      tokenStore.setCustomer(data.customers[0].id);
      setActiveCustomerIdState(data.customers[0].id);
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (tokenStore.get()) {
        try {
          await loadMe();
        } catch (e) {
          logout();
        }
      }
      setLoading(false);
    })();
  }, [loadMe, logout]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      tokenStore.set(data.token);
      await loadMe();
      return data.user;
    },
    [loadMe],
  );

  const setActiveCustomerId = useCallback((id) => {
    tokenStore.setCustomer(id);
    setActiveCustomerIdState(id);
  }, []);

  const value = useMemo(() => {
    const activeCustomer = user?.role === "proxima_admin" ? customers.find((c) => c.id === activeCustomerId) || null : customer;
    return {
      user,
      customer: activeCustomer,
      customers,
      activeCustomerId,
      setActiveCustomerId,
      loading,
      login,
      logout,
      refresh: loadMe,
      isProxima: user?.role === "proxima_admin",
      isAdmin: user?.role === "proxima_admin" || user?.role === "customer_admin",
    };
  }, [user, customer, customers, activeCustomerId, setActiveCustomerId, loading, login, logout, loadMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
